
import React, { useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { toast } from "sonner";
import JSConfetti from 'js-confetti';

interface TrackCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackId: string;
  trackName: string;
  isSendingNotification?: boolean;
}

const TrackCreatedModal: React.FC<TrackCreatedModalProps> = ({
  isOpen,
  onClose,
  trackId,
  trackName,
  isSendingNotification = false
}) => {
  const [copied, setCopied] = React.useState(false);
  const shareUrl = `${window.location.origin}/download?id=${trackId}`;

  // Confetti effect when modal is shown
  useEffect(() => {
    if (isOpen) {
      const jsConfetti = new JSConfetti();
      jsConfetti.addConfetti({
        emojis: ['🎵', '⚡️', '💥', '✨', '🎤', '🔊'],
        emojiSize: 30,
      });
    }
  }, [isOpen]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const openShareUrl = () => {
    window.open(shareUrl, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>🎉 Congratulations!</DialogTitle>
          <DialogDescription>
            Your track "{trackName}" has been created and can now be shared.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex items-center space-x-2 mt-4">
          <div className="grid flex-1 gap-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Share Link
            </label>
            <input
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={shareUrl}
              readOnly
            />
          </div>
          <Button type="button" size="icon" onClick={copyToClipboard} disabled={copied}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        
        {isSendingNotification && (
          <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
            Sending notification email...
          </div>
        )}
        
        <DialogFooter className="sm:justify-between mt-6">
          <Button 
            variant="outline"
            onClick={openShareUrl}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Open URL
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrackCreatedModal;
